import cx from 'clsx';
import React, { useRef } from 'react';
import { Navigation, Pagination, Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/swiper.min.css';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as EditIcon } from '@assets/svg/ic-edit-pencil.svg';
import { ReactComponent as SwiperArrowIcon } from '@assets/svg/ic-swiper-arrow.svg';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { getUrlResizeImage } from '@helpers/getUrlResizeImage';

import type { IFile } from '@model/file';

import { useModalActions } from '@store/useModalStore';

import PhotoLabel from '../PhotoLabel/PhotoLabel';
import EmptyGalleryBlock from './components/EmptyGalleryBlock/EmptyGalleryBlock';
import style from './style.module.scss';

interface IProps {
  images?: Array<IFile>;
  variant?: string;
  editEnabled?: boolean;
  onEditPhotos?: () => void;
}

const Gallery: React.FC<IProps> = ({ images = [], variant, editEnabled = false, onEditPhotos }) => {
  const swiperRef = useRef<SwiperType>();

  const { setActiveModal } = useModalActions();

  const handleOpenLightbox = (index: number) => {
    const imagesUrlsArr = images.map((image) => {
      return { src: image.url, sourceFrom: image.sourceFrom };
    });

    setActiveModal({
      name: MODAL_NAMES.lightboxModal,
      options: { images: imagesUrlsArr, index },
    });
  };

  const showEditBtn = editEnabled && !!onEditPhotos;

  if (!images?.length) {
    return <EmptyGalleryBlock editEnabled={editEnabled} onEditPhotos={onEditPhotos} />;
  }

  return (
    <div className={style.galleryWrapper}>
      <Swiper
        className={cx(style.gallery, { [style.equipmentGallery]: variant === 'equipment' })}
        modules={[Pagination, Navigation]}
        spaceBetween={15}
        slidesPerView={1}
        pagination={{ clickable: true }}
        observer={true}
        breakpoints={{
          576: {
            slidesPerView: 3,
            direction: 'horizontal',
          },
          1000: {
            slidesPerView: 1,
          },
        }}
        onBeforeInit={(swiper) => {
          swiperRef.current = swiper;
        }}
      >
        {images.map((image, i) => (
          <SwiperSlide key={uuidv4()}>
            <img
              src={getUrlResizeImage(image.url, image.noResize ? '' : '300_400')}
              className={style.galleryImage}
              alt={`image-${i}`}
              onClick={() => handleOpenLightbox(i)}
            />
            <PhotoLabel sourceFrom={image.sourceFrom} />
          </SwiperSlide>
        ))}
        <button className={cx(style.navBtn, style.prevBtn)} onClick={() => swiperRef.current?.slidePrev()}>
          <SwiperArrowIcon className={style.prevBtnIcon} />
        </button>
        <button className={cx(style.navBtn, style.nextBtn)} onClick={() => swiperRef.current?.slideNext()}>
          <SwiperArrowIcon className={style.nextBtnIcon} />
        </button>
      </Swiper>
      {showEditBtn && !!images.length && (
        <div className={style.editIconWrapper} onClick={onEditPhotos}>
          <EditIcon className={style.editIcon} />
        </div>
      )}
      <div className={style.printPhotosBlock}>
        {images.map((image) => (
          <img key={uuidv4()} className={style.printPhoto} src={image.url} alt={image.name} />
        ))}
      </div>
    </div>
  );
};

export default Gallery;
